import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import {
  Video, Save, Info, Link2, Unlink, TestTube2, CheckCircle2, XCircle, Globe, Zap,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, ConfirmModal, FormField, NativeSelect,
} from "../ui";
import "./settings.scss";

/**
 * The three ways an institute can run Zoom. Only one is ever in use, so this is
 * a dropdown and the chosen one's bullets render underneath — the three cards
 * that used to sit side by side described two modes the institute had not
 * picked and could not act on.
 */
const ZOOM_MODES = [
  {
    value: "v1",
    label: "V1 — Plain Link (Legacy)",
    points: [
      "Instructor opens Zoom in a new tab via meeting link",
      "Students join via embedded SDK inside the platform",
      "Zoom meeting link visible to instructors",
    ],
  },
  {
    value: "v2",
    label: "V2 — Full Embedded SDK (Recommended)",
    points: [
      "Instructor joins as host via embedded SDK inside the platform",
      "Students join via embedded SDK inside the platform",
      "No Zoom links visible to anyone",
      "Unified in-app experience for all users",
    ],
  },
  {
    value: "platformHosted",
    label: "Platform Hosted (Managed)",
    points: [
      "No Zoom account setup required — managed by the platform",
      "Zoom meetings are created on-the-fly when class starts",
      "The number of simultaneous classes is set by your plan",
    ],
  },
];

/** V2 talks to a different Zoom OAuth app per environment. */
const V2_ENVIRONMENTS = [
  { value: "dev", label: "Development — Zoom development OAuth app, for testing" },
  { value: "prod", label: "Production — Zoom production OAuth app, for live usage" },
];

/**
 * ZoomSettings — Admin tab for Zoom integration configuration (SaaS multi-tenant).
 *
 * Mounted by LiveClassSettings, which owns the provider dropdown above it, so
 * everything here is about Zoom and nothing else.
 *
 * Sections, each shown only when the chosen mode uses it:
 *   1. Integration mode (V1 / V2 / Platform Hosted) — a dropdown
 *   2. V2 environment (dev / prod) and auto-create — V2 only
 *   3. The Zoom account connection for that mode — platform-hosted needs none
 *   4. Platform status (SDK + webhook from .env)
 *   5. Save
 */
const ZoomSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Per-mode loading states
  const [v1Testing, setV1Testing] = useState(false);
  const [v2Testing, setV2Testing] = useState(false);
  const [v1Connecting, setV1Connecting] = useState(false);
  const [v2Connecting, setV2Connecting] = useState(false);
  const [v1Disconnecting, setV1Disconnecting] = useState(false);
  const [v2Disconnecting, setV2Disconnecting] = useState(false);
  const [v1TestResult, setV1TestResult] = useState(null);
  const [v2TestResult, setV2TestResult] = useState(null);

  // Which mode is currently connecting via OAuth (for postMessage handler)
  const [pendingOAuthMode, setPendingOAuthMode] = useState(null);

  const [form, setForm] = useState({
    zoomMode: "v1",
    v2Mode: "dev",
    v2AutoCreateMeeting: false,
    // V1 connection
    v1Connected: false,
    v1ZoomEmail: "",
    v1ZoomUserId: "",
    // V2 connection
    v2Connected: false,
    v2ZoomEmail: "",
    v2ZoomUserId: "",
    // Platform
    sdkConfigured: false,
    webhookConfigured: false,
    // Platform-Hosted
    platformHostedConfigured: false,
  });

  const loadSettings = useCallback(() => {
    setLoading(true);
    apiClient
      .get("/institute/zoom-settings")
      .then(({ data }) => {
        setForm({
          zoomMode: data.zoomMode || "v1",
          v2Mode: data.v2Mode || "dev",
          v2AutoCreateMeeting: data.v2AutoCreateMeeting || false,
          v1Connected: data.v1Connected || false,
          v1ZoomEmail: data.v1ZoomEmail || "",
          v1ZoomUserId: data.v1ZoomUserId || "",
          v2Connected: data.v2Connected || false,
          v2ZoomEmail: data.v2ZoomEmail || "",
          v2ZoomUserId: data.v2ZoomUserId || "",
          sdkConfigured: data.sdkConfigured || false,
          webhookConfigured: data.webhookConfigured || false,
          platformHostedConfigured: data.platformHostedConfigured || false,
        });
      })
      .catch(() => {
        setToast({ show: true, message: "Failed to load Zoom settings.", type: "danger" });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Listen for OAuth popup completion
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === "ZOOM_OAUTH_COMPLETE") {
        const mode = pendingOAuthMode || "v1";
        if (mode === "v2") setV2Connecting(false);
        else setV1Connecting(false);
        setPendingOAuthMode(null);

        if (event.data.status === "success") {
          const label = mode === "v2" ? "V2" : "V1";
          setToast({ show: true, message: `${label} Zoom account connected successfully!`, type: "success" });
          loadSettings();
        } else {
          setToast({ show: true, message: "Zoom connection failed. Please try again.", type: "danger" });
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [loadSettings, pendingOAuthMode]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/institute/zoom-settings", {
        zoomMode: form.zoomMode,
        v2Mode: form.v2Mode,
        v2AutoCreateMeeting: form.v2AutoCreateMeeting,
      });
      setToast({ show: true, message: "Zoom settings saved successfully!", type: "success" });
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to save Zoom settings.";
      setToast({ show: true, message: msg, type: "danger" });
    }
    setSaving(false);
  };

  const openOAuthPopup = (url) => {
    const w = 600, h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    window.open(url, "zoom_oauth", `width=${w},height=${h},left=${left},top=${top}`);
  };

  const handleConnect = async (mode) => {
    if (mode === "v2") setV2Connecting(true);
    else setV1Connecting(true);
    setPendingOAuthMode(mode);

    try {
      const { data } = await apiClient.get(`/institute/zoom-settings/oauth-url?mode=${mode}`);
      if (data.url) openOAuthPopup(data.url);
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to start Zoom connection.";
      setToast({ show: true, message: msg, type: "danger" });
      if (mode === "v2") setV2Connecting(false);
      else setV1Connecting(false);
      setPendingOAuthMode(null);
    }
  };

  const [disconnectTarget, setDisconnectTarget] = useState(null);

  const handleDisconnect = (mode) => setDisconnectTarget(mode);

  const confirmDisconnect = async () => {
    const mode = disconnectTarget;
    setDisconnectTarget(null);
    const label = mode === "v2" ? "V2" : "V1";

    if (mode === "v2") setV2Disconnecting(true);
    else setV1Disconnecting(true);

    try {
      await apiClient.delete(`/institute/zoom-settings/disconnect?mode=${mode}`);
      setToast({ show: true, message: `${label} Zoom account disconnected.`, type: "success" });
      loadSettings();
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to disconnect Zoom account.";
      setToast({ show: true, message: msg, type: "danger" });
    }
    if (mode === "v2") setV2Disconnecting(false);
    else setV1Disconnecting(false);
  };

  const handleTest = async (mode) => {
    if (mode === "v2") { setV2Testing(true); setV2TestResult(null); }
    else { setV1Testing(true); setV1TestResult(null); }

    try {
      const { data } = await apiClient.post("/institute/zoom-settings/test", { mode });
      const result = { success: true, message: `${data.message} (${data.zoomEmail})` };
      if (mode === "v2") setV2TestResult(result);
      else setV1TestResult(result);
    } catch (ex) {
      const msg = ex?.response?.data?.error || ex?.response?.data?.message || "Connection test failed.";
      const result = { success: false, message: msg };
      if (mode === "v2") setV2TestResult(result);
      else setV1TestResult(result);
    }
    if (mode === "v2") setV2Testing(false);
    else setV1Testing(false);
  };

  /** Reusable connection card for V1 or V2 */
  const renderConnectionSection = (mode) => {
    const isV2 = mode === "v2";
    const connected = isV2 ? form.v2Connected : form.v1Connected;
    const email = isV2 ? form.v2ZoomEmail : form.v1ZoomEmail;
    const userId = isV2 ? form.v2ZoomUserId : form.v1ZoomUserId;
    const isTesting = isV2 ? v2Testing : v1Testing;
    const isConnecting = isV2 ? v2Connecting : v1Connecting;
    const isDisconnecting = isV2 ? v2Disconnecting : v1Disconnecting;
    const testResult = isV2 ? v2TestResult : v1TestResult;
    const label = isV2 ? "V2" : "V1";

    return (
      <AppCard key={mode}>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Link2 size={18} /> {label} Zoom Account Connection
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            {isV2
              ? "Connect the Zoom account used for V2 embedded SDK instructor hosting"
              : "Connect the Zoom account used for V1 recording downloads and meeting management"}
          </p>
        </AppCardHeader>
        <AppCardContent>
          {connected ? (
            <div className="zoom-connected-info">
              <div className="zoom-connected-info__status">
                <CheckCircle2 size={20} className="text-success" />
                <div>
                  <strong>{label} Zoom Account Connected</strong>
                  <p className="mb-0 text-muted">{email || userId || "Connected"}</p>
                </div>
              </div>
              <div className="zoom-connected-info__actions">
                <AppButton variant="outline" size="sm" onClick={() => handleTest(mode)} disabled={isTesting}>
                  {isTesting ? <><Spinner size="sm" className="me-1" /> Testing…</> :
                    <><TestTube2 size={14} className="me-1" /> Test Connection</>}
                </AppButton>
                <AppButton variant="outline-danger" size="sm" onClick={() => handleDisconnect(mode)} disabled={isDisconnecting}>
                  {isDisconnecting ? <><Spinner size="sm" className="me-1" /> Disconnecting…</> :
                    <><Unlink size={14} className="me-1" /> Disconnect</>}
                </AppButton>
              </div>
            </div>
          ) : (
            <div className="zoom-connect-prompt">
              <p className="text-muted mb-3">
                {isV2
                  ? "No V2 Zoom account connected. Connect to enable embedded SDK instructor hosting."
                  : "No V1 Zoom account connected. Connect to enable recording downloads and meeting management."}
              </p>
              <AppButton onClick={() => handleConnect(mode)} disabled={isConnecting}>
                {isConnecting ? <><Spinner size="sm" className="me-1" /> Connecting…</> :
                  <><Link2 size={16} className="me-1" /> Connect {label} Zoom Account</>}
              </AppButton>
            </div>
          )}

          {testResult && (
            <div className={`zoom-test-result mt-3 ${testResult.success ? "zoom-test-result--success" : "zoom-test-result--error"}`}>
              {testResult.success ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              {testResult.message}
            </div>
          )}
        </AppCardContent>
      </AppCard>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner size="sm" /> Loading Zoom settings…
      </div>
    );
  }

  return (
    <div className="zoom-settings">
      {/* ─── Integration Mode ─── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Video size={18} /> Zoom Integration Mode
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Choose how instructors and students join Zoom meetings
          </p>
        </AppCardHeader>
        <AppCardContent>
          {/* A dropdown, not three cards. Only one mode is ever in use, and the
              two that are not were three screenfuls of bullet points explaining
              behaviour this institute had not chosen. */}
          <FormField label="Mode" className="zoom-field-narrow">
            <NativeSelect
              value={form.zoomMode}
              onChange={(e) => setForm((f) => ({ ...f, zoomMode: e.target.value }))}
              options={ZOOM_MODES.map((m) => ({ value: m.value, label: m.label }))}
            />
          </FormField>

          <ul className="zoom-mode-card__list">
            {(ZOOM_MODES.find((m) => m.value === form.zoomMode) || ZOOM_MODES[0]).points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>

          {form.zoomMode === "platformHosted" && (
            <div className="zoom-info-box">
              <Info size={16} />
              <div>
                <strong>Platform Hosted Mode:</strong> Zoom meetings are created automatically when an
                instructor starts a class. No Zoom account connection is needed. The number of concurrent
                live classes is limited by your plan.
                {/* Deliberately no pool size. How many Zoom hosts the platform
                    owns is OUR capacity, not this institute's entitlement — and
                    a number that moves for reasons no customer can see invites
                    exactly the wrong question. Their limit is their plan. */}
                {form.platformHostedConfigured ? (
                  <span className="d-block mt-1 text-success">
                    <CheckCircle2 size={14} className="me-1" />
                    Platform Zoom is configured and ready
                  </span>
                ) : (
                  <span className="d-block mt-1 text-danger">
                    <XCircle size={14} className="me-1" />
                    Platform Zoom not configured. Contact the platform admin.
                  </span>
                )}
              </div>
            </div>
          )}

          {form.zoomMode === "v2" && (
            <div className="zoom-info-box">
              <Info size={16} />
              <div>
                <strong>V2 Requirements:</strong> Each batch must have a{" "}
                <code>Zoom Meeting ID</code> and optional <code>Passcode</code> configured.
                The institute's V2 Zoom account must be connected below.
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* ─── V2 Environment (Dev / Prod) ─── shown only when V2 is the active mode */}
      {form.zoomMode === "v2" && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>
              <span className="settings-card-title">
                <Globe size={18} /> V2 Environment
              </span>
            </AppCardTitle>
            <p className="settings-card-desc">
              Choose the Zoom OAuth app environment for V2 integration
            </p>
          </AppCardHeader>
          <AppCardContent>
            <FormField label="Environment" className="zoom-field-narrow">
              <NativeSelect
                value={form.v2Mode}
                onChange={(e) => setForm((f) => ({ ...f, v2Mode: e.target.value }))}
                options={V2_ENVIRONMENTS}
              />
            </FormField>

            <div className="zoom-info-box mt-3">
              <Info size={16} />
              <div>
                <strong>Note:</strong> Changing the V2 environment affects which Zoom OAuth app
                is used for authorization. You may need to reconnect the V2 Zoom account after switching.
              </div>
            </div>
          </AppCardContent>
        </AppCard>
      )}

      {/* ─── V2 Auto-Create Meeting Toggle ─── shown only when V2 is the active mode */}
      {form.zoomMode === "v2" && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>
              <span className="settings-card-title">
                <Zap size={18} /> Auto-Create Zoom Meetings
              </span>
            </AppCardTitle>
            <p className="settings-card-desc">
              Automatically create a Zoom meeting when a new online/hybrid batch is created
            </p>
          </AppCardHeader>
          <AppCardContent>
            <div className="d-flex align-items-center gap-3">
              <div
                className="form-check form-switch"
                style={{ transform: "scale(1.3)", marginLeft: "4px" }}
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="v2AutoCreateMeeting"
                  checked={form.v2AutoCreateMeeting}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, v2AutoCreateMeeting: e.target.checked }))
                  }
                />
              </div>
              <label htmlFor="v2AutoCreateMeeting" style={{ cursor: "pointer" }}>
                <strong>{form.v2AutoCreateMeeting ? "Enabled" : "Disabled"}</strong>
                <p className="mb-0 text-muted" style={{ fontSize: "0.85rem" }}>
                  {form.v2AutoCreateMeeting
                    ? "Zoom Meeting ID, passcode, and link will be auto-filled when you create a new batch with Online or Both mode."
                    : "You will need to manually enter Zoom Meeting ID and passcode for each batch."}
                </p>
              </label>
            </div>

            {form.v2AutoCreateMeeting && !form.v2Connected && (
              <div className="zoom-info-box mt-3" style={{ borderColor: "#dc3545" }}>
                <XCircle size={16} className="text-danger" />
                <div className="text-danger">
                  <strong>V2 Zoom account not connected.</strong> Auto-create requires an active V2 connection.
                  Please connect below.
                </div>
              </div>
            )}

            {form.v2AutoCreateMeeting && form.v2Connected && (
              <div className="zoom-info-box mt-3">
                <Info size={16} />
                <div>
                  <strong>Zoom scope required:</strong> <code>meeting:write:meeting</code> — this must be
                  enabled on your Zoom app. The meeting is created under the connected V2 account.
                </div>
              </div>
            )}
          </AppCardContent>
        </AppCard>
      )}

      {/* ─── Zoom Account Connection ─── only the one the chosen mode uses.
           Platform-hosted needs no institute account at all, so it gets none:
           a "Connect V1 Zoom Account" button under a managed mode is an
           invitation to set up something that will never be read. */}
      {form.zoomMode === "v1" && renderConnectionSection("v1")}
      {form.zoomMode === "v2" && renderConnectionSection("v2")}

      {/* ─── Platform Status ─── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Info size={18} /> Platform Configuration
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Global settings managed by the platform admin (configured in server environment)
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="zoom-platform-status">
            <div className="zoom-platform-status__item">
              <span>Meeting SDK (Embedded Join)</span>
              {form.sdkConfigured ? (
                <span className="zoom-source-badge zoom-source-badge--db"><CheckCircle2 size={12} /> Configured</span>
              ) : (
                <span className="zoom-source-badge zoom-source-badge--none"><XCircle size={12} /> Not configured</span>
              )}
            </div>
            <div className="zoom-platform-status__item">
              <span>Webhook Secret</span>
              {form.webhookConfigured ? (
                <span className="zoom-source-badge zoom-source-badge--db"><CheckCircle2 size={12} /> Configured</span>
              ) : (
                <span className="zoom-source-badge zoom-source-badge--none"><XCircle size={12} /> Not configured</span>
              )}
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ─── Save ─── */}
      <div className="zoom-actions">
        <AppButton onClick={handleSave} disabled={saving}>
          {saving ? <><Spinner size="sm" className="me-1" /> Saving…</> :
            <><Save size={16} className="me-1" /> Save Zoom Settings</>}
        </AppButton>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!disconnectTarget}
        onHide={() => setDisconnectTarget(null)}
        onConfirm={confirmDisconnect}
        title="Disconnect Zoom"
        message={`Are you sure you want to disconnect the ${disconnectTarget === "v2" ? "V2" : "V1"} Zoom account?`}
        confirmText="Disconnect"
        variant="warning"
      />
    </div>
  );
};

export default ZoomSettings;
